import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  useToast,
  Icon,
  VStack,
  Text,
  Badge,
  HStack,
  Spinner,
  useDisclosure,
} from '@chakra-ui/react';
import { FiMessageSquare } from 'react-icons/fi';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { app, db } from '../../../utils/init-firebase';
import { useAuth } from '../../../contexts/AuthContext';
import useCalScheduling from '../../../hooks/useCalScheduling';
import SendTestSMSModal from './SendTestSMSModal';
import SmsMediaPicker from './SmsMediaPicker';
import DynamicVariableInsertButton from '../Email/DynamicVariableInsertButton';
import { buildVariableMap, resolveTemplateVariables, insertTagIntoTextarea } from '../../../utils/templateVariables';

// Utility to format phone for display
const formatPhoneForDisplay = (phone) => {
  if (!phone) return '';
  const digitsOnly = phone.replace(/\D/g, '');
  
  // US format: (XXX) XXX-XXXX
  if (digitsOnly.length === 10) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
  }
  
  // US with country code
  if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    const usNumber = digitsOnly.slice(1);
    return `+1 (${usNumber.slice(0, 3)}) ${usNumber.slice(3, 6)}-${usNumber.slice(6)}`;
  }
  
  return phone;
};

export default function DialpadSMSButton({ 
  phoneNumber, 
  contactName,
  dealId = null,
  contactId = null,
  deal = null,
  contact = null,
  size = 'sm',
  isDisabled = false,
  initialMessage = '',
  buttonLabel = 'Text (Dialpad)',
  buttonColorScheme = 'green',
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [message, setMessage] = useState('');
  const [media, setMedia] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const messageRef = useRef(null);
  const toast = useToast();
  const { currentUser } = useAuth();
  const { defaultBookingUrl } = useCalScheduling();

  // Validate phone number
  const hasValidPhone = phoneNumber && phoneNumber.replace(/\D/g, '').length >= 10;

  // Load SMS templates from Firestore
  useEffect(() => {
    const templatesRef = collection(db, 'smsTemplates');
    const q = query(templatesRef, where('isActive', '==', true));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const templatesList = [];
      snapshot.forEach((doc) => {
        templatesList.push({ id: doc.id, ...doc.data() });
      });
      setTemplates(templatesList);
      setLoadingTemplates(false);
    });

    return () => unsubscribe();
  }, []);

  // When no contact object is passed, fall back to the display name/phone props
  const effectiveContact = useMemo(() => {
    if (contact) return { ...contact, phone: contact.phone || phoneNumber };
    if (contactName || phoneNumber) {
      return {
        firstName: contactName?.split(' ')[0] || '',
        lastName: contactName?.split(' ').slice(1).join(' ') || '',
        phone: phoneNumber || '',
      };
    }
    return null;
  }, [contact, contactName, phoneNumber]);

  // Full variable map: every contact/deal field (flat + dot-notation) + sender.
  const variableMap = useMemo(() => buildVariableMap({
    contact: effectiveContact,
    deal,
    sender: {
      name: currentUser?.displayName || currentUser?.email || '',
      email: currentUser?.email || '',
      phone: '',
      bookingLink: defaultBookingUrl || '',
    },
  }), [effectiveContact, deal, currentUser, defaultBookingUrl]);

  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      // Fill known values; unknown tags stay visible and are resolved
      // server-side by sendDialpadSMS at send time.
      setMessage(resolveTemplateVariables(template.body, variableMap, { stripUnknown: false }));
    }
  };

  const handleSend = async () => {
    if (!message.trim() && !media) {
      toast({
        title: 'Message or image required',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    if (!hasValidPhone) {
      toast({
        title: 'Invalid phone number',
        description: 'Phone number must have at least 10 digits',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setIsSending(true);
    try {
      const functions = getFunctions(app, 'us-east1');
      const sendSMS = httpsCallable(functions, 'sendDialpadSMS');
      
      const result = await sendSMS({
        phoneNumber,
        message: message.trim(),
        dealId,
        contactId,
        mediaUrl: media?.url || null,
      });

      if (result.data.success) {
        toast({
          title: media ? 'MMS sent' : 'SMS sent',
          description: `Message sent to ${contactName || phoneNumber} via Dialpad`,
          status: 'success',
          duration: 4000,
        });

        setMessage('');
        setMedia(null);
        onClose();
      }
    } catch (error) {
      console.error('Error sending SMS:', error);
      
      let errorMessage = error.message;
      if (error.message.includes('not enabled') || error.message.includes('failed-precondition')) {
        errorMessage = 'Dialpad is not enabled for your account. Please enable it in CRM Settings.';
      } else if (error.message.includes('Do Not Contact')) {
        errorMessage = 'This contact is marked as Do Not Contact (DNC).';
      } else if (error.message.includes('Rate limit') || error.message.includes('resource-exhausted')) {
        errorMessage = 'Rate limit exceeded. Please wait a moment and try again.';
      } else if (error.message.includes('Invalid phone number')) {
        errorMessage = error.message; // Use the detailed phone validation error
      } else if (error.message.includes('authentication failed') || error.message.includes('permission-denied')) {
        errorMessage = 'Dialpad authentication failed. Please contact your administrator.';
      } else if (error.message.includes('Dialpad API error')) {
        errorMessage = error.message; // Pass through Dialpad API errors
      }
      
      toast({
        title: 'Could not send SMS',
        description: errorMessage,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsSending(false);
    }
  };

  const charCount = message.length;
  const charColor = charCount > 160 ? 'orange' : charCount > 140 ? 'yellow' : 'gray';

  const formattedPhone = formatPhoneForDisplay(phoneNumber);

  return (
    <>
      <Button
        leftIcon={<Icon as={FiMessageSquare} />}
        size={size}
        w="full"
        colorScheme={buttonColorScheme}
        onClick={() => {
          // Prefill (e.g. "Send Booking Link") without clobbering a draft
          if (initialMessage && !message.trim()) setMessage(initialMessage);
          onOpen();
        }}
        isDisabled={isDisabled || !hasValidPhone}
        variant="solid"
      >
        {buttonLabel}
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'md' }}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={2}>
              <Icon as={FiMessageSquare} />
              <Text>Send SMS via Dialpad</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                To: <Text as="span" fontWeight="semibold">{contactName || formattedPhone}</Text>
              </Text>
              <Text fontSize="xs" color="gray.500">
                Phone: {formattedPhone}
              </Text>

              <FormControl>
                <FormLabel fontSize="sm">Template</FormLabel>
                {loadingTemplates ? (
                  <HStack p={2}>
                    <Spinner size="sm" />
                    <Text fontSize="sm">Loading templates...</Text>
                  </HStack>
                ) : templates.length > 0 ? (
                  <Select
                    placeholder="Select a template (optional)"
                    value={selectedTemplate}
                    onChange={(e) => handleTemplateChange(e.target.value)}
                    size="sm"
                  >
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <Text fontSize="xs" color="gray.400">No SMS templates configured</Text>
                )}
                <Text fontSize="xs" color="gray.500" mt={1} wordBreak="break-word">
                  All contact, deal, and sender variables are supported — use the insert
                  button or type e.g. {'{{firstName}}, {{deal.loanAmount}}, {{loanOfficerName}}'}
                </Text>
              </FormControl>

              <FormControl>
                <HStack justify="space-between" mb={2}>
                  <FormLabel fontSize="sm" mb={0}>
                    Message
                  </FormLabel>
                  <HStack spacing={2}>
                    <Badge colorScheme={charColor}>
                      {charCount} chars
                    </Badge>
                    <DynamicVariableInsertButton
                      onInsert={(tag) => insertTagIntoTextarea(messageRef, message, tag, setMessage)}
                    />
                  </HStack>
                </HStack>
                <Textarea
                  ref={messageRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your message..."
                  rows={6}
                  maxLength={320}
                />
                {charCount > 160 && (
                  <Text fontSize="xs" color="orange.600" mt={1}>
                    Message may be sent as {Math.ceil(charCount / 160)} parts
                  </Text>
                )}
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Image (optional)</FormLabel>
                <SmsMediaPicker media={media} onChange={setMedia} isDisabled={isSending} />
                {media && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Sends as an MMS. Charges and delivery depend on the recipient's carrier.
                  </Text>
                )}
              </FormControl>

              <Text fontSize="xs" color="gray.500">
                This message will be sent through Dialpad and automatically logged in the deal timeline.
              </Text>
            </VStack>
          </ModalBody>

          <ModalFooter flexWrap="wrap" gap={2}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="outline"
              colorScheme="blue"
              onClick={() => setIsTestModalOpen(true)}
              isDisabled={!message.trim() && !media}
            >
              Send Test SMS
            </Button>
            <Button
              colorScheme="green"
              leftIcon={<FiMessageSquare />}
              onClick={handleSend}
              isLoading={isSending}
              loadingText="Sending..."
              isDisabled={!message.trim() && !media}
            >
              Send via Dialpad
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <SendTestSMSModal
        isOpen={isTestModalOpen}
        onClose={() => setIsTestModalOpen(false)}
        message={resolveTemplateVariables(message, variableMap)}
        mediaUrl={media?.url || null}
      />
    </>
  );
}

